// repo.js — single write path for all domain mutations.
// Online: call the server API → cache server record in Dexie → return server data.
// Offline (or network failure): write optimistically to Dexie + queue in outbox.
// The client UUID stored in every outbox payload is used by the server's applyOp
// so that ON CONFLICT (id) DO NOTHING keeps both sides in sync after push+pull.
// HTTP errors (4xx/5xx) are always re-thrown regardless of online state.

import db from '../db/localdb';
import { productAPI, purchaseAPI, stockAPI, saleAPI, customerAPI, expenseAPI, productionAPI } from '../services/api';
import SyncManager from './SyncManager';
import { lineTotalCents, toCents, centsToString } from '../utils/money';

const genId  = () => crypto.randomUUID();
const isoNow = () => new Date().toISOString();

async function enqueue(op_id, entity, op_type, payload) {
  await db.outbox.add({ op_id, entity, op_type, payload, synced_at: null, created_at: isoNow() });
}

// Try the API. Returns the axios response on success, null on network failure.
// Re-throws HTTP errors (4xx/5xx) so callers still see validation errors.
async function tryOnline(apiCall) {
  if (!navigator.onLine) return null;
  try {
    return await apiCall();
  } catch (err) {
    if (err.response) throw err;
    return null;
  }
}

// After any online write, kick the SyncManager so it pulls the latest state.
function nudgeSync() {
  setTimeout(() => SyncManager.sync(), 200);
}

// ── Products ─────────────────────────────────────────────────────────────────

export async function createProduct(formData) {
  const res = await tryOnline(() => productAPI.create(formData));
  if (res) {
    const p = res.data.product;
    if (p) await db.products.put(p);
    nudgeSync();
    return res.data;
  }
  const id    = genId();
  const op_id = genId();
  const record = { id, ...formData, stock_qty: 0, voided_at: null, version: 0, created_at: isoNow(), updated_at: isoNow() };
  await db.products.put(record);
  await enqueue(op_id, 'product', 'create', { id, ...formData, op_id });
  return { product: record, _offline: true };
}

export async function updateProduct(id, formData) {
  const res = await tryOnline(() => productAPI.update(id, formData));
  if (res) {
    const p = res.data.product;
    if (p) await db.products.put(p);
    nudgeSync();
    return res.data;
  }
  const op_id = genId();
  await db.products.where('id').equals(id).modify({ ...formData, updated_at: isoNow() });
  await enqueue(op_id, 'product', 'update', { id, ...formData, op_id });
  return { _offline: true };
}

export async function voidProduct(id) {
  const res = await tryOnline(() => productAPI.void(id));
  if (res) {
    await db.products.where('id').equals(id).modify({ voided_at: isoNow() });
    nudgeSync();
    return res.data;
  }
  const op_id = genId();
  await db.products.where('id').equals(id).modify({ voided_at: isoNow(), updated_at: isoNow() });
  await enqueue(op_id, 'product', 'void', { id, op_id });
  return { _offline: true };
}

// ── Purchases (restock) ──────────────────────────────────────────────────────

export async function createPurchase(formData) {
  const res = await tryOnline(() => purchaseAPI.create(formData));
  if (res) {
    const p = res.data.product;
    if (p) await db.products.put(p);
    nudgeSync();
    return res.data;
  }
  const id    = genId();
  const op_id = genId();
  // Every queued op carries the time it really happened, so a purchase made
  // offline isn't dated when the phone next finds a signal.
  const purchased_at = formData.purchased_at || isoNow();
  const record = {
    id, ...formData,
    voided_at: null, version: 0,
    purchased_at, created_at: isoNow(), updated_at: isoNow(),
  };
  await db.purchases.put(record);
  await db.products.where('id').equals(formData.product_id).modify(p => {
    p.stock_qty = (Number(p.stock_qty) || 0) + Number(formData.qty);
  });
  await enqueue(op_id, 'purchase', 'create', { id, ...formData, purchased_at, op_id });
  return { _offline: true };
}

// Own produce — self-produced stock (no purchase cost). Raises stock without
// recording purchase spend; recorded server-side as a zero-cost adjustment.
export async function createOwnProduce(formData) {
  const res = await tryOnline(() => stockAPI.ownProduce(formData));
  if (res) {
    const p = res.data.product;
    if (p) await db.products.put(p);
    nudgeSync();
    return res.data;
  }
  const id    = genId();
  const op_id = genId();
  await db.products.where('id').equals(formData.product_id).modify(p => {
    p.stock_qty = (Number(p.stock_qty) || 0) + Number(formData.qty);
  });
  await enqueue(op_id, 'stock_adjustment', 'create', {
    id, product_id: formData.product_id, qty: Number(formData.qty),
    reason: 'Own produce', created_at: formData.produced_at || isoNow(), op_id,
  });
  return { _offline: true };
}

// ── Sales ────────────────────────────────────────────────────────────────────

// Promote a walk-in buyer to a saved customer so they show up under Customers
// and accrue a payment-history rating. Reuses an existing customer when the name
// already matches (case-insensitive) to avoid duplicate rows for repeat buyers.
// Returns the customer id — a server id when online, a local UUID when offline.
// Because createCustomer enqueues the customer op first, a later sale op that
// references this id flushes AFTER it, so the customer exists before the sale on
// the server (ops are applied in outbox order).
async function ensureCustomerForBuyer(name, contact) {
  const trimmed = (name || '').trim();
  if (!trimmed) return null;
  const key = trimmed.toLowerCase();
  const existing = await db.customers
    .filter(c => !c.voided_at && (c.name || '').trim().toLowerCase() === key)
    .first();
  if (existing) return existing.id;
  const res = await createCustomer({ name: trimmed, contact: contact || null });
  return res.customer?.id || null;
}

export async function createSale(formData) {
  // Capture the buyer as a customer unless the sale is already linked to one.
  const data = { ...formData };
  if (!data.customer_id && data.buyer_name) {
    data.customer_id = await ensureCustomerForBuyer(data.buyer_name, data.buyer_contact);
  }

  const res = await tryOnline(() => saleAPI.create(data));
  if (res) {
    const s = res.data.sale;
    if (s) await db.sales.put(s);
    nudgeSync();
    return res.data;
  }
  const id    = genId();
  const op_id = genId();
  const sold_at = data.sold_at || isoNow();
  // Same exact total and derived status the server will compute.
  const totalCents = lineTotalCents(data.qty, data.unit_price);
  const paidCents  = data.payment_status === 'paid' ? totalCents : toCents(data.amount_paid || 0);
  const record = {
    id, ...data,
    total_amount: centsToString(totalCents),
    amount_paid:  centsToString(paidCents),
    payment_status: paidCents >= totalCents ? 'paid' : paidCents > BigInt(0) ? 'partial' : 'debt',
    voided_at: null, version: 0,
    sold_at, created_at: isoNow(), updated_at: isoNow(),
  };
  await db.sales.put(record);
  await db.products.where('id').equals(data.product_id).modify(p => {
    p.stock_qty = (Number(p.stock_qty) || 0) - Number(data.qty);
  });
  await enqueue(op_id, 'sale', 'create', { id, ...data, sold_at, op_id });
  return { sale: record, _offline: true };
}

// Void a sale (owner/manager). Stock and money figures follow on the server.
export async function voidSale(id, reason) {
  const res = await tryOnline(() => saleAPI.void(id, { reason }));
  if (res) {
    await db.sales.where('id').equals(id).modify({ voided_at: isoNow() });
    nudgeSync();
    return res.data;
  }
  const op_id = genId();
  const sale = await db.sales.get(id);
  await db.sales.where('id').equals(id).modify({ voided_at: isoNow(), updated_at: isoNow() });
  if (sale) {
    await db.products.where('id').equals(sale.product_id).modify(p => {
      p.stock_qty = (Number(p.stock_qty) || 0) + Number(sale.qty);
    });
  }
  await enqueue(op_id, 'sale', 'void', { id, reason, op_id });
  return { _offline: true };
}

// Void one payment (owner/manager) — e.g. a mobile-money payment that bounced.
export async function voidPayment(id, reason) {
  const res = await tryOnline(() => saleAPI.voidPayment(id, { reason }));
  if (res) {
    await db.sale_payments.where('id').equals(id).modify({ voided_at: isoNow() });
    if (res.data.sale) await db.sales.put(res.data.sale);
    nudgeSync();
    return res.data;
  }
  const op_id = genId();
  await db.sale_payments.where('id').equals(id).modify({ voided_at: isoNow(), updated_at: isoNow() });
  await enqueue(op_id, 'sale_payment', 'void', { id, reason, op_id });
  return { _offline: true };
}

export async function addPayment(saleId, formData) {
  const res = await tryOnline(() => saleAPI.addPayment(saleId, formData));
  if (res) {
    nudgeSync();
    return res.data;
  }
  const id    = genId();
  const op_id = genId();
  const paid_at = formData.paid_at || isoNow();
  const record = {
    id, sale_id: saleId, ...formData, paid_at, kind: 'repayment',
    voided_at: null, version: 0,
    created_at: isoNow(), updated_at: isoNow(),
  };
  await db.sale_payments.put(record);
  // Optimistically reflect it on the sale until the server confirms.
  await db.sales.where('id').equals(saleId).modify(s => {
    const paid = toCents(s.amount_paid) + toCents(formData.amount);
    s.amount_paid = centsToString(paid);
    s.payment_status = paid >= toCents(s.total_amount) ? 'paid' : 'partial';
  });
  await enqueue(op_id, 'sale_payment', 'create', { id, sale_id: saleId, ...formData, paid_at, op_id });
  return { payment: record, _offline: true };
}

// ── Customers ────────────────────────────────────────────────────────────────

export async function createCustomer(formData) {
  const res = await tryOnline(() => customerAPI.create(formData));
  if (res) {
    const c = res.data.customer;
    if (c) await db.customers.put(c);
    nudgeSync();
    return res.data;
  }
  const id    = genId();
  const op_id = genId();
  const record = { id, ...formData, rating: null, voided_at: null, version: 0, created_at: isoNow(), updated_at: isoNow() };
  await db.customers.put(record);
  await enqueue(op_id, 'customer', 'create', { id, ...formData, op_id });
  return { customer: record, _offline: true };
}

export async function updateCustomer(id, formData) {
  const res = await tryOnline(() => customerAPI.update(id, formData));
  if (res) {
    const c = res.data.customer;
    if (c) await db.customers.put(c);
    nudgeSync();
    return res.data;
  }
  const op_id = genId();
  await db.customers.where('id').equals(id).modify({ ...formData, updated_at: isoNow() });
  await enqueue(op_id, 'customer', 'update', { id, ...formData, op_id });
  return { _offline: true };
}

// ── Expenses ─────────────────────────────────────────────────────────────────

export async function createExpense(formData) {
  const res = await tryOnline(() => expenseAPI.create(formData));
  if (res) {
    const e = res.data.expense;
    if (e) await db.expenses.put(e);
    nudgeSync();
    return res.data;
  }
  const id    = genId();
  const op_id = genId();
  const record = {
    id, ...formData,
    voided_at: null, version: 0,
    expense_at: formData.expense_at || isoNow(),
    created_at: isoNow(), updated_at: isoNow(),
  };
  await db.expenses.put(record);
  await enqueue(op_id, 'expense', 'create', { id, ...formData, expense_at: record.expense_at, op_id });
  return { expense: record, _offline: true };
}

export async function updateExpense(id, formData) {
  const res = await tryOnline(() => expenseAPI.update(id, formData));
  if (res) {
    const e = res.data.expense;
    if (e) await db.expenses.put(e);
    nudgeSync();
    return res.data;
  }
  const op_id = genId();
  await db.expenses.where('id').equals(id).modify({ ...formData, updated_at: isoNow() });
  await enqueue(op_id, 'expense', 'update', { id, ...formData, op_id });
  return { _offline: true };
}

export async function voidExpense(id) {
  const res = await tryOnline(() => expenseAPI.void(id));
  if (res) {
    await db.expenses.where('id').equals(id).modify({ voided_at: isoNow() });
    nudgeSync();
    return res.data;
  }
  const op_id = genId();
  await db.expenses.where('id').equals(id).modify({ voided_at: isoNow(), updated_at: isoNow() });
  await enqueue(op_id, 'expense', 'void', { id, op_id });
  return { _offline: true };
}

// ── Production runs (bottling / assembly) ────────────────────────────────────

export async function createProductionRun(formData) {
  const res = await tryOnline(() => productionAPI.create(formData));
  if (res) {
    const run = res.data.production_run;
    if (run) await db.production_runs.put(run);
    nudgeSync();
    return res.data;
  }
  // Offline: optimistic local record only (inputs embedded in payload for UI display)
  const id    = genId();
  const op_id = genId();
  const record = {
    id, ...formData,
    voided_at: null, version: 0,
    produced_at: formData.produced_at || isoNow(),
    created_at: isoNow(), updated_at: isoNow(),
  };
  await db.production_runs.put(record);
  // Optimistically adjust stock: finished good up, each input down
  await db.products.where('id').equals(formData.product_id).modify(p => {
    p.stock_qty = (Number(p.stock_qty) || 0) + Number(formData.qty_produced);
  });
  for (const input of (formData.inputs || [])) {
    if (!input.product_id || !input.qty_used) continue;
    await db.products.where('id').equals(input.product_id).modify(p => {
      p.stock_qty = (Number(p.stock_qty) || 0) - Number(input.qty_used);
    });
  }
  await enqueue(op_id, 'production_run', 'create', { id, ...formData, produced_at: record.produced_at, op_id });
  return { production_run: record, _offline: true };
}

export async function voidProductionRun(id) {
  const res = await tryOnline(() => productionAPI.void(id));
  if (res) {
    await db.production_runs.where('id').equals(id).modify({ voided_at: isoNow() });
    nudgeSync();
    return res.data;
  }
  const op_id = genId();
  await db.production_runs.where('id').equals(id).modify({ voided_at: isoNow(), updated_at: isoNow() });
  await enqueue(op_id, 'production_run', 'void', { id, op_id });
  return { _offline: true };
}

// ── Outbox state (for SyncStatusBadge) ──────────────────────────────────────

// Waiting to be sent (excludes ops the server refused).
export async function pendingCount() {
  return db.outbox.filter(op => !op.synced_at && !op.failed_at).count();
}

// Ops the server permanently refused — shown to the user so a record that
// never reached the server doesn't silently look saved.
export async function failedOps() {
  return db.outbox.filter(op => !!op.failed_at && !op.dismissed_at).toArray();
}

export async function dismissFailedOp(localId) {
  await db.outbox.update(localId, { dismissed_at: isoNow() });
}
